import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { api } from '../lib/api';

export function ExecutorRunDetailsPage() {
  const { runId } = useParams();
  const [run, setRun] = useState(null);
  const [intel, setIntel] = useState(null);
  const [intelLoading, setIntelLoading] = useState(false);
  const [intelError, setIntelError] = useState(null);
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let mounted = true;
    async function load() {
      if (!runId) return;
      setLoading(true);
      setError(null);
      try {
        const res = await api.get(`/api/pentest-executor/runs/${encodeURIComponent(runId)}`);
        if (!mounted) return;
        setRun(res);
      } catch (e) {
        if (!mounted) return;
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (mounted) setLoading(false);
      }
    }
    load();
    return () => {
      mounted = false;
    };
  }, [runId]);

  useEffect(() => {
    let mounted = true;
    async function loadIntelligence() {
      if (!run?.run_id) return;
      const customerId = run.customer_id || 'default';
      setIntelLoading(true);
      setIntelError(null);
      try {
        const res = await api.get(
          `/api/run-intelligence/${encodeURIComponent(customerId)}/overview/${encodeURIComponent(run.run_id)}`,
          { timeoutMs: 20000 }
        );
        if (!mounted) return;
        setIntel(res);
      } catch (e) {
        if (!mounted) return;
        setIntelError(e instanceof Error ? e.message : String(e));
      } finally {
        if (mounted) setIntelLoading(false);
      }
    }
    loadIntelligence();
    return () => {
      mounted = false;
    };
  }, [run?.customer_id, run?.run_id]);

  const created = run?.created_at ? new Date(run.created_at) : null;
  const citationTypeCounts = (intel?.citations || []).reduce((acc, c) => {
    const t = c?.artifact_type || 'unknown';
    acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  async function downloadContractSnapshot() {
    if (!run?.run_id) return;
    const customerId = run.customer_id || 'default';
    const scenarioId = run.lineage?.scenario_id || run.scenario_id;
    const params = new URLSearchParams();
    if (scenarioId) params.set('scenario_id', String(scenarioId));
    const qs = params.toString();
    const path = `/api/run-intelligence/${encodeURIComponent(customerId)}/snapshot/${encodeURIComponent(run.run_id)}${qs ? `?${qs}` : ''}`;
    setSnapshotBusy(true);
    try {
      const data = await api.get(path, { timeoutMs: 60000 });
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `aerta-run-contract-${run.run_id}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setSnapshotBusy(false);
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h1 className="font-heading text-3xl font-bold text-foreground">Executor run</h1>
          <p className="text-muted-foreground">
            Full record for a single allowlisted executor scan.
          </p>
        </div>
        <Button asChild variant="outline" size="sm">
          <Link to="/runs">← Back to runs</Link>
        </Button>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading run details…</p>}
      {error && (
        <p className="text-sm text-red-500">
          {error}
        </p>
      )}

      {!loading && !error && run && (
        <>
          <Card>
            <CardHeader>
              <CardTitle className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-sm">{run.run_id}</span>
                <Badge variant="outline" className="text-xs">
                  {run.status || 'completed'}
                </Badge>
                {run.backend && (
                  <Badge variant="outline" className="text-xs">
                    backend: {run.backend}
                  </Badge>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm text-muted-foreground">
              <div>
                <span className="font-medium text-foreground">When:</span>{' '}
                {created ? created.toLocaleString() : '—'}
              </div>
              <div>
                <span className="font-medium text-foreground">Targets:</span>{' '}
                {(run.target_cidrs || []).join(', ') || '—'}
              </div>
              <div>
                <span className="font-medium text-foreground">Ports:</span>{' '}
                {(run.ports || []).join(', ') || 'default profile'}
              </div>
              <div>
                <span className="font-medium text-foreground">Result:</span>{' '}
                {run.hosts_found ?? 0} host(s) with {run.open_ports_total ?? 0} open port(s)
              </div>
              {run.bridge_url && (
                <div>
                  <span className="font-medium text-foreground">Bridge URL:</span>{' '}
                  <code className="rounded bg-secondary px-1 text-xs">{run.bridge_url}</code>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="grid gap-4 md:grid-cols-2">
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex flex-wrap items-center gap-2">
                    Run intelligence
                    <Badge variant={intel?.grounded ? 'default' : 'outline'} className="text-xs">
                      {intel?.grounded ? 'grounded' : 'not grounded'}
                    </Badge>
                  </span>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={snapshotBusy || !run?.run_id}
                    onClick={() => void downloadContractSnapshot()}
                  >
                    {snapshotBusy ? 'Exporting…' : 'Export contract snapshot'}
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                {intelLoading && <p className="text-muted-foreground">Loading citation status…</p>}
                {intelError && <p className="text-red-500">{intelError}</p>}
                {!intelLoading && !intelError && intel && (
                  <>
                    <p className="text-muted-foreground">
                      {intel.note || 'Run correlation overview loaded.'}
                    </p>
                    {intel.lineage_completeness ? (
                      <div className="rounded-md border border-border/60 p-2">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-xs font-medium text-muted-foreground">Lineage completeness</span>
                          <Badge
                            variant="outline"
                            className="text-xs"
                          >
                            {intel.lineage_completeness.score}%
                          </Badge>
                          <Badge
                            variant={intel.lineage_completeness.band === 'high' ? 'default' : 'outline'}
                            className="text-xs"
                          >
                            {intel.lineage_completeness.band}
                          </Badge>
                        </div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {Object.entries(intel.lineage_completeness.checks || {}).map(([k, v]) => (
                            <Badge key={k} variant="outline" className="text-xs">
                              {k}: {v ? 'yes' : 'no'}
                            </Badge>
                          ))}
                        </div>
                        {intel.next_actions && intel.next_actions.length > 0 ? (
                          <div className="mt-2">
                            <div className="text-xs font-medium text-muted-foreground">Next actions</div>
                            <ul className="mt-1 list-disc pl-5 text-xs text-muted-foreground">
                              {intel.next_actions.slice(0, 4).map((a) => (
                                <li key={a}>{a}</li>
                              ))}
                            </ul>
                          </div>
                        ) : null}
                      </div>
                    ) : null}
                    {(!intel.citations || intel.citations.length === 0) && (
                      <p className="text-xs text-muted-foreground">No citations indexed for this run yet.</p>
                    )}
                    {intel.citations && intel.citations.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground">
                          Citations ({intel.totals?.citations ?? intel.citations.length})
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(citationTypeCounts).map(([t, n]) => (
                            <Badge key={t} variant="outline" className="text-xs">
                              {t}: {n}
                            </Badge>
                          ))}
                        </div>
                        {intel.citations.slice(0, 8).map((c) => (
                          <div key={c.artifact_id} className="rounded-md border border-border/60 p-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <code className="rounded bg-secondary px-1 text-xs">{c.artifact_id}</code>
                              <Badge variant="outline" className="text-xs">{c.artifact_type}</Badge>
                              {c.artifact_type === 'defense_signal_record' ? (
                                <Badge variant="outline" className="text-xs">defense signal evidence</Badge>
                              ) : null}
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              run_id: {c.run_id || '—'} | source: {c.source_timestamp || '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {intel.defense_signals && intel.defense_signals.length > 0 && (
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground">
                          Correlated defense signals ({intel.totals?.defense_signals ?? intel.defense_signals.length})
                        </div>
                        {intel.defense_signals.slice(0, 6).map((s) => (
                          <div key={s.defense_signal_id} className="rounded-md border border-border/60 p-2">
                            <div className="flex flex-wrap items-center gap-2">
                              <code className="rounded bg-secondary px-1 text-xs">{s.defense_signal_id}</code>
                              <Badge variant="outline" className="text-xs">{s.action || 'triage'}</Badge>
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              item: {s.item_id || '—'} | playbook: {s.playbook_id || '—'}
                            </div>
                            <div className="mt-1 text-xs text-muted-foreground">
                              scenario: {s.lineage?.scenario_id || '—'} | run: {s.lineage?.run_id || '—'}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <CardTitle>Hosts &amp; ports</CardTitle>
              </CardHeader>
              <CardContent>
                {(!run.parsed_hosts || run.parsed_hosts.length === 0) && (
                  <p className="text-sm text-muted-foreground">No hosts with open ports were recorded.</p>
                )}
                {run.parsed_hosts && run.parsed_hosts.length > 0 && (
                  <div className="space-y-3 text-sm">
                    {run.parsed_hosts.map((h) => (
                      <div key={h.ip} className="rounded-md border border-border/60 p-2">
                        <div className="flex items-center justify-between">
                          <div className="font-mono text-xs text-foreground">{h.ip}</div>
                          {h.hostname && (
                            <div className="text-xs text-muted-foreground">{h.hostname}</div>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-muted-foreground">
                          Open ports:{' '}
                          {(h.open_ports || []).length > 0
                            ? (h.open_ports || []).join(', ')
                            : 'none'}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Command &amp; logs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">nmap argv</div>
                  <pre className="max-h-32 overflow-auto rounded bg-secondary/40 p-2 text-xs">
                    {JSON.stringify(run.nmap_argv || [], null, 2)}
                  </pre>
                </div>
                <div>
                  <div className="mb-1 text-xs font-medium text-muted-foreground">stdout (truncated)</div>
                  <pre className="max-h-40 overflow-auto rounded bg-secondary/40 p-2 text-xs">
                    {run.stdout_truncated || '—'}
                  </pre>
                </div>
                {run.stderr_truncated && (
                  <div>
                    <div className="mb-1 text-xs font-medium text-muted-foreground">stderr (truncated)</div>
                    <pre className="max-h-32 overflow-auto rounded bg-secondary/40 p-2 text-xs">
                      {run.stderr_truncated}
                    </pre>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </>
      )}
    </div>
  );
}

